import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import InfoChip from './InfoChip';

function TenantMessages() {
  const { user, authReady } = useContext(AuthContext);
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [selectedAppId, setSelectedAppId] = useState('');
  const [selectedThreadType, setSelectedThreadType] = useState('');
  const [threadFilter, setThreadFilter] = useState('all');
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState('');
  const [chatText, setChatText] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [allMessages, setAllMessages] = useState([]);
  const [allLoading, setAllLoading] = useState(false);
  const [lastViewedAt, setLastViewedAt] = useState(null);
  const [supportThread, setSupportThread] = useState(null);
  const [supportMessages, setSupportMessages] = useState([]);
  const [supportText, setSupportText] = useState('');
  const [supportSending, setSupportSending] = useState(false);
  const [supportError, setSupportError] = useState('');

  useEffect(() => {
    const fetchApplications = async () => {
      if (!authReady) return;
      if (!user || user.userType !== 'tenant') {
        navigate('/login');
        return;
      }

      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/applications`, {
          headers: { 'Authorization': `Bearer ${user.token}` },
        });
        if (!res.ok) throw new Error('Failed to load applications');
        const data = await res.json();
        const apps = data.applications || [];
        setApplications(apps);
        if (!selectedAppId && apps.length > 0) {
          const approved = apps.find(app => app.status === 'approved');
          setSelectedAppId((approved || apps[0])?.id || '');
        }
      } catch (err) {
        setChatError(err.message || 'Could not load your applications.');
      } finally {
        setLoading(false);
      }
    };

    fetchApplications();
  }, [user, authReady, navigate, selectedAppId]);

  useEffect(() => {
    const fetchSupportMessages = async () => {
      if (!user || !user.token) return;
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/messages/support`, {
          headers: { 'Authorization': `Bearer ${user.token}` }
        });
        if (!res.ok) return;
        const data = await res.json().catch(() => ({}));
        setSupportThread(data.thread || null);
        setSupportMessages(data.messages || []);
      } catch (err) {
        console.error('Could not load support messages', err);
      }
    };

    fetchSupportMessages();
  }, [user]);

  useEffect(() => {
    if (selectedThreadType) return;
    if (supportThread || supportMessages.length > 0) {
      setSelectedThreadType('support');
      return;
    }
    if (applications.length > 0) {
      setSelectedThreadType('application');
    }
  }, [supportThread, supportMessages.length, applications.length, selectedThreadType]);

  useEffect(() => {
    const fetchAllMessages = async () => {
      if (!user || !user.token || applications.length === 0) return;
      setAllLoading(true);
      try {
        const [countRes, ...commentResults] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/messages/unread-count`, {
            headers: { 'Authorization': `Bearer ${user.token}` },
          }),
          ...applications.map((app) =>
            fetch(`${process.env.REACT_APP_API_BASE_URL}/api/applications/${app.id}/comments`, {
              headers: { 'Authorization': `Bearer ${user.token}` },
            }).then(async (res) => ({ res, data: await res.json().catch(() => ({})), app }))
          )
        ]);

        if (countRes.ok) {
          const countData = await countRes.json().catch(() => ({}));
          setLastViewedAt(countData.last_viewed_at);
        }

        const results = commentResults.map(({ res, data, app }) => {
          if (!res.ok) return [];
          return (data.comments || []).map((comment) => ({
            ...comment,
            application_id: app.id,
            property_address: app.property_address
          }));
        });

        const flattened = results.flat().sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setAllMessages(flattened);
      } catch (err) {
        setChatError(err.message || 'Could not load all messages.');
      } finally {
        setAllLoading(false);
      }
    };

    fetchAllMessages();
  }, [user, applications]);

  const selectedApplication = useMemo(
    () => applications.find(app => String(app.id) === String(selectedAppId)),
    [applications, selectedAppId]
  );

  useEffect(() => {
    const fetchChat = async () => {
      if (!user || !user.token || !selectedApplication?.id) return;
      setChatLoading(true);
      setChatError('');
      try {
        const [countRes, chatRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/messages/unread-count`, {
            headers: { 'Authorization': `Bearer ${user.token}` },
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/applications/${selectedApplication.id}/comments`, {
            headers: { 'Authorization': `Bearer ${user.token}` },
          })
        ]);

        if (countRes.ok) {
          const countData = await countRes.json().catch(() => ({}));
          setLastViewedAt(countData.last_viewed_at);
        }

        if (!chatRes.ok) throw new Error('Failed to load chat.');
        const data = await chatRes.json();
        setChatMessages((data.comments || []).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
        await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/messages/mark-viewed`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${user.token}` }
        });
        window.dispatchEvent(new Event('messages:viewed'));
      } catch (err) {
        setChatError(err.message || 'Could not load chat.');
      } finally {
        setChatLoading(false);
      }
    };

    if (selectedThreadType === 'application') {
      fetchChat();
    }
  }, [user, selectedApplication?.id, selectedThreadType]);

  const handleSendChat = async () => {
    if (!chatText.trim() || chatSending || !selectedApplication?.id) return;
    setChatSending(true);
    setChatError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/applications/${selectedApplication.id}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${user.token}` },
        body: JSON.stringify({ comment_text: chatText.trim(), origin: 'lease' })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to send message');
      setChatMessages((prev) => [data.comment, ...prev]);
      setAllMessages((prev) => ([{ ...data.comment, application_id: selectedApplication.id, property_address: selectedApplication.property_address }, ...prev]));
      setChatText('');
    } catch (err) {
      setChatError(err.message || 'Could not send message.');
    } finally {
      setChatSending(false);
    }
  };

  const handleSupportSend = async () => {
    if (!supportText.trim() || supportSending) return;
    setSupportSending(true);
    setSupportError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/messages/support/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${user.token}` },
        body: JSON.stringify({ message_text: supportText.trim() })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to send message');
      setSupportMessages((prev) => [data.message, ...prev]);
      setSupportText('');
    } catch (err) {
      setSupportError(err.message || 'Could not send message.');
    } finally {
      setSupportSending(false);
    }
  };

  const latestByApp = useMemo(() => {
    const map = {};
    for (const msg of allMessages) {
      if (!map[msg.application_id]) {
        map[msg.application_id] = msg;
      }
    }
    return map;
  }, [allMessages]);

  const applicationThreads = useMemo(() => {
    return applications
      .map((app) => {
        const latest = latestByApp[app.id];
        return {
          type: 'application',
          id: app.id,
          title: app.property_address || 'Application',
          subtitle: app.status || 'Application',
          lastMessage: latest?.comment_text || '',
          lastAt: latest?.created_at || app.created_at
        };
      })
      .sort((a, b) => new Date(b.lastAt || 0) - new Date(a.lastAt || 0));
  }, [applications, latestByApp]);

  const supportThreadItem = useMemo(() => {
    if (!supportThread && supportMessages.length === 0) return null;
    const latest = supportMessages[0];
    return {
      type: 'support',
      id: 'support',
      title: 'Support',
      subtitle: 'Rentual support',
      lastMessage: latest?.message_text || 'No messages yet.',
      lastAt: latest?.created_at || supportThread?.created_at
    };
  }, [supportThread, supportMessages]);

  const threadItems = useMemo(() => {
    const items = [supportThreadItem, ...applicationThreads].filter(Boolean);
    if (threadFilter === 'support') return items.filter(item => item.type === 'support');
    if (threadFilter === 'applications') return items.filter(item => item.type === 'application');
    return items;
  }, [supportThreadItem, applicationThreads, threadFilter]);

  const selectedThreadIsSupport = selectedThreadType === 'support';

  const handleSelectThread = (thread) => {
    if (thread.type === 'support') {
      setSelectedThreadType('support');
      return;
    }
    setSelectedThreadType('application');
    setSelectedAppId(String(thread.id));
  };

  if (loading) {
    return <main className="App-main">Loading messages...</main>;
  }

  return (
    <main className="App-main tenants-page">
      <div className="ledger-header">
        <div>
          <p className="eyebrow-tag">Messages</p>
          <h2>Messages</h2>
          <p>All conversations in one inbox.</p>
          <InfoChip label="Inbox tips">
            Use Support for Rentual questions and the property threads for landlord-only chat.
          </InfoChip>
        </div>
      </div>

      <div className="messages-layout">
        <div className="dashboard-section messages-panel">
          <p className="status-text" style={{ marginBottom: '0.35rem' }}>Filter:</p>
          <div className="form-actions messages-filters">
            <button className={`${threadFilter === 'all' ? 'primary-button' : 'secondary-button'} segment-button`} onClick={() => setThreadFilter('all')}>All</button>
            <button className={`${threadFilter === 'support' ? 'primary-button' : 'secondary-button'} segment-button`} onClick={() => setThreadFilter('support')}>Support</button>
            <button className={`${threadFilter === 'applications' ? 'primary-button' : 'secondary-button'} segment-button`} onClick={() => setThreadFilter('applications')}>Chats</button>
          </div>
          <p className="status-text" style={{ margin: '0.75rem 0 0.5rem' }}>Chat: select a thread to view messages.</p>
          {allLoading && threadItems.length === 0 ? (
            <p className="status-text">Loading threads...</p>
          ) : threadItems.length === 0 ? (
            <p className="status-text">No conversations yet.</p>
          ) : (
            <div className="inquiry-list">
              {threadItems.map((thread) => {
                const isSelected = (thread.type === 'support' && selectedThreadIsSupport) || (thread.type === 'application' && String(thread.id) === String(selectedAppId));
                return (
                  <div
                    key={`${thread.type}-${thread.id}`}
                    className="inquiry-card"
                    onClick={() => handleSelectThread(thread)}
                    style={{ cursor: 'pointer', border: isSelected ? '1px solid #2f6fed' : undefined }}
                  >
                    <p><strong>{thread.title}</strong></p>
                    <p className="status-text">{thread.subtitle}</p>
                    <p className="status-text" style={{ marginTop: '0.35rem' }}>{thread.lastMessage || 'No messages yet.'}</p>
                    {thread.lastAt && <p className="timestamp">{new Date(thread.lastAt).toLocaleString()}</p>}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="dashboard-section messages-panel">
          {selectedThreadIsSupport ? (
            <>
              <div className="section-header">
                <h3>Messages:</h3>
                <span className="report-chip report-chip--muted">Support</span>
              </div>
              {supportMessages.length === 0 ? (
                <p className="status-text">No support messages yet.</p>
              ) : (
                <div className="lease-chat-thread">
                  {supportMessages.map((msg) => (
                    <div key={`support-${msg.id}`} className={`lease-chat-message ${msg.sender_type === 'support' ? 'landlord' : 'tenant'}`}>
                      <p>{msg.message_text}</p>
                      <span>{msg.sender_type === 'support' ? 'Support' : 'You'}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="lease-chat-compose" style={{ marginTop: '0.75rem' }}>
                {supportError && <p className="error-message">{supportError}</p>}
                <InfoChip label="Support">
                  Only Rentual support sees this thread—landlords don’t get these messages.
                </InfoChip>
                <textarea
                  className="comment-box"
                  rows="2"
                  value={supportText}
                  onChange={(e) => setSupportText(e.target.value)}
                  placeholder="Message support..."
                />
                <button className="secondary-button" onClick={handleSupportSend} disabled={supportSending || !supportText.trim()}>
                  {supportSending ? 'Sending...' : 'Send message'}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="section-header">
                <h3>{selectedApplication ? selectedApplication.property_address : 'Application'}</h3>
                {selectedApplication?.status && (
                  <span className="report-chip report-chip--muted">{selectedApplication.status}</span>
                )}
              </div>
              {!selectedApplication && <p className="status-text">Select a conversation to view messages.</p>}
              {selectedApplication && (
                <>
                  {chatError && <p className="error-message">{chatError}</p>}
                  <div className="lease-chat-thread">
                    {chatLoading ? (
                      <p className="status-text">Loading chat...</p>
                    ) : chatMessages.length === 0 ? (
                      <p className="status-text">No messages yet. Start the conversation below.</p>
                    ) : (
                      chatMessages.map((msg) => {
                        const isUnread = lastViewedAt && new Date(msg.created_at) > new Date(lastViewedAt) && msg.user_type === 'landlord';
                        return (
                          <div key={msg.id} className={`lease-chat-message ${msg.user_type} ${isUnread ? 'unread' : ''}`}>
                            <p>{msg.comment_text}</p>
                            <span>{msg.user_type === 'landlord' ? 'Owner' : 'Tenant'}</span>
                          </div>
                        );
                      })
                    )}
                  </div>
                  <div className="lease-chat-compose">
                    <InfoChip label="Landlord chat">
                      Messages here go straight to the landlord. Keep support issues in the Support tab.
                    </InfoChip>
                    <textarea
                      className="comment-box"
                      rows="3"
                      value={chatText}
                      onChange={(e) => setChatText(e.target.value)}
                      placeholder="Write a message to your landlord..."
                    />
                    <button className="primary-button" onClick={handleSendChat} disabled={chatSending || !chatText.trim()}>
                      {chatSending ? 'Sending...' : 'Send Message'}
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </main>
  );
}

export default TenantMessages;
